import { memo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import { capitalize } from 'utils'
import { themeV5 } from 'theme/v5'

const getStyles = (name, selected) => ({
  fontWeight:
    selected === name === -1
      ? themeV5.typography.fontWeightRegular
      : themeV5.typography.fontWeightMedium,
})

const SingleSelect = ({
  id,
  label,
  options = [],
  noNone,
  customNoneLabel,
  tight,
  value = '',
  onChange,
  size,
  hideLabelOnMobile,
  disabled,
  ...rest
}) => {

  const [selected, setSelected] = useState(value)
  const [labelWidth, setLabelWidth] = useState(0)
  const labelRef = useRef()

  const onChangeSelection = event =>
    onChange(event.target.value)

  useEffect(() => {
    setLabelWidth(hideLabelOnMobile ? 0 : labelRef.current.offsetWidth)
  }, [hideLabelOnMobile])

  useEffect(() => {
    setSelected(value)
  }, [value])

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
      }}>
      <FormControl
        sx={{
          width: '100%',
          ...( tight && {
            borderRadius: 4,
            border: '1px solid #ced4da',
            '&:focus': {
              borderRadius: 4,
              borderColor: '#80bdff',
              boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
              '& > div': {
                background: themeV5.palette.background.paper,
              },
            },
            '& label': {
              background: 'none !important', // theme.palette.background.paper,
            },
            '& .MuiInputBase-input': {
              background: 'none !important',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10.8px 16px',
            },
            '& .MuiInputLabel-outlined': {
              transform: 'translate(14px, 14px) scale(1)',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            },
          })
        }}
        size={size}
        variant="outlined">
        <InputLabel
          sx={{
            backgroundColor: 'transparent',
          }}
          style={{ color: disabled ? '#DDDDDD' : '#000000' }}
          ref={labelRef}
          htmlFor={`select-multiple-${id}`}
          {...rest}>
          {hideLabelOnMobile ? null : label}
        </InputLabel>
        <Select
          disabled={disabled}
          value={selected}
          onChange={onChangeSelection}
          labelWidth={labelWidth}
          inputProps={{
            id,
            name: label,
          }}
          {...rest}>
          { ! noNone && (
            <MenuItem value="">
              <em>{customNoneLabel || 'No Filter'}</em>
            </MenuItem>
          )}
          {options.filter(o => o).map((option, index) => {

            const name = option.label || option
            const code = option.value || option

            if (option.label && ! option.value) {
              return (
                <MenuItem
                  sx={{
                    paddingTop: '2px !important',
                    paddingBottom: '2px !important',
                    minHeight: '0 !important',
                    background: 'transparent !important',
                  }}
                  key={index}
                  disabled
                  value="">
                  <em>{capitalize(name)}</em>
                </MenuItem>
              )
            }

            return (
              <MenuItem
                key={index}
                value={code}
                style={getStyles(code, selected)}>
                {capitalize(name)}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>
    </div>
  )
}

SingleSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  noNone: PropTypes.bool,
  customNoneLabel: PropTypes.string,
  tight: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  size: PropTypes.string,
  hideLabelOnMobile: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default memo(SingleSelect)
